import type { RouteItem } from "@/types/route"
import { isObject } from "./is"

export type MenuEntity = RouteItem & {
  id: string
  path: string
  parentId?: string | null
  children?: MenuEntity[]
}

export type MenuCaches = {
  pathMap: Map<string, MenuEntity>
  ancestorsMap: Map<string, string[]>
  routeToMenuPathMap: Map<string, string>
}

/**
 * Key ổn định được sử dụng cho mục menu ở thanh bên (tương thích với menu cấp 1 có path có thể trống)
 * Dùng để giữ ancestorsMap, Menu openKeys đồng bộ với key của mục menu, đảm bảo sau khi làm mới trang cha có thể tự động mở rộng chính xác
 */
export function getMenuKey(node: { path?: string; id?: string; redirect?: string }): string {
  const path = node.path?.trim()
  if (path) return path
  if (node.id) return String(node.id)
  const redirect = node.redirect?.trim()
  if (redirect) return redirect
  return String(node.id ?? "")
}

/**
 * Thêm đối tượng dưới dạng tham số vào URL
 * @param baseUrl url gốc
 * @param obj đối tượng chứa các cặp key-value tham số
 * @returns {string} url sau khi đã thêm tham số
 * ví dụ:
 *  let obj = {a: '3', b: '4'}
 *  setObjToUrlParams('www.baidu.com', obj)
 *  ==>www.baidu.com?a=3&b=4
 */
export function setObjToUrlParams(baseUrl: string, obj: any): string {
  let parameters = ""
  for (const key in obj) {
    parameters += `${key}=${encodeURIComponent(obj[key])}&`
  }
  parameters = parameters.replace(/&$/, "")
  return /\?$/.test(baseUrl) ? baseUrl + parameters : baseUrl.replace(/\/?$/, "?") + parameters
}

export function deepMerge<T = object>(src: Record<string, any> = {}, target: any = {}): T {
  let key: string
  for (key in target) {
    if (isObject(src[key])) {
      src[key] = deepMerge(src[key], target[key])
    } else {
      src[key] = target[key]
    }
  }
  return src as T
}

/**
 * @description Tìm kiếm đệ quy route tương ứng
 * @param path Đường dẫn hiện tại cần truy cập
 * @param routes Danh sách các route
 * @returns RouteItem hoặc null
 */
export const searchRoute = (path: string, routes: RouteItem[] = []): RouteItem | null => {
  for (const item of routes) {
    if (item.path === path) {
      return item
    }
    if (item.children) {
      const res = searchRoute(path, item.children)
      if (res) {
        return res
      }
    }
  }
  return null
}

/** Kiểu nút menu được dùng cho getFirstMenuPath (chỉ cần path/route/children) */
type MenuNodeForFirstPath = { path?: string; route?: boolean; children?: MenuNodeForFirstPath[] }

/**
 * Lấy mục "route" đầu tiên từ cây menu (nhất quán với logic trang chủ khi đăng nhập chọn vai trò)
 * @param menus Cây menu
 * @returns Đường dẫn (path) của menu đầu tiên có route là true, nếu không tìm thấy trả về null
 */
export function getFirstMenuPath(menus: MenuNodeForFirstPath[]): string | null {
  for (const menu of menus) {
    if (menu.route && menu.path?.trim()) {
      return menu.path.trim()
    }
    if (menu.children?.length) {
      const found = getFirstMenuPath(menu.children)
      if (found) {
        return found
      }
    }
  }
  return null
}

/**
 * Ánh xạ dữ liệu lấy từ backend thành dữ liệu có chứa thuộc tính key, dùng cho các React component liên quan
 * @param data Dữ liệu đầu vào
 * @param key Trường duy nhất trong dữ liệu dùng để làm key
 * @returns Dữ liệu đã được ánh xạ bổ sung key
 */
export const addKeyToData = (data: any[], key: string) => {
  return data.map((item) => {
    const newItem = { ...item, key: item[key] }
    if (item.children) {
      newItem.children = addKeyToData(item.children, key)
    }
    return newItem
  })
}

/**
 * Lấy nhãn hiển thị của phím tắt
 * @param shortcut Chuỗi phím tắt
 * @returns Nhãn phím tắt đã được định dạng
 */
export function getShortcutLabel(shortcut: string): string {
  const isMac = (navigator as any).userAgentData?.platform === "macOS"
  return shortcut
    .replace("ctrl", isMac ? "⌘" : "Ctrl")
    .replace("shift", isMac ? "⇧" : "Shift")
    .replace("alt", isMac ? "⌥" : "Alt")
    .replace("meta", isMac ? "⌘" : "Meta") // Tùy chọn;
}

/**
 * Chuyển đổi dữ liệu cho Tree component
 * @param data Dữ liệu Tree component
 * @param expanded Các nút đang được mở rộng
 * @param t Hàm dịch đa ngôn ngữ (i18n)
 * @returns Dữ liệu đã được chuyển đổi
 */
export function transformData(data: any[], expanded: string[], t: (key: string) => string) {
  return data.map((item: any) => {
    const newItem = {
      ...item,
      icon: item.icon,
      originalIcon: item.icon,
      name: t(item.name),
    }

    if (item.children?.length > 0) {
      expanded.push(item.id)
      newItem.children = transformData(item.children, expanded, t)
    }

    return newItem
  })
}

/**
 * Mô phỏng logic so khớp đường dẫn (pathname matching) của TanStack Router
 * @param routeDef Đường dẫn định nghĩa của route (Ví dụ: /users/$userId/settings)
 * @param currentPath Đường dẫn thực tế hiện tại (Ví dụ: /users/123/settings)
 * @param exact Có so khớp chính xác hoàn toàn hay không (Mặc định là true. Nếu là false, /users/$id có thể khớp với /users/123/details)
 */
export function matchPathname(routeDef: string, currentPath: string, exact = true): boolean {
  // 1. Loại bỏ các dấu gạch chéo ở cuối và phân tách đường dẫn thành các phần
  const cleanRoute = routeDef.replace(/\/+$/, "").split("/").filter(Boolean)
  const cleanCurrent = currentPath?.split("?")?.[0]?.replace(/\/+$/, "").split("/").filter(Boolean) ?? []

  // 2. Nếu so khớp chính xác (exact), độ dài hai mảng phân tách phải bằng nhau
  // Nếu so khớp không chính xác (so khớp tiền tố), độ dài đường dẫn thực tế phải lớn hơn hoặc bằng đường dẫn định nghĩa
  if (exact) {
    if (cleanRoute.length !== cleanCurrent.length) {
      return false
    }
  } else {
    if (cleanCurrent.length < cleanRoute.length) {
      return false
    }
  }

  // 3. So sánh từng đoạn đường dẫn
  for (let i = 0; i < cleanRoute.length; i++) {
    const routeSegment = cleanRoute[i]
    const currentSegment = cleanCurrent[i]

    // Xử lý ký tự đại diện (splat routes - ví dụ '*')
    if (routeSegment === "*") {
      return true
    }

    // Xử lý tham số động ($userId)
    if (routeSegment?.startsWith("$")) {
      continue // Chỉ cần vị trí này có giá trị, coi như khớp thành công
    }

    // Các phần tĩnh (static segments) bắt buộc phải bằng nhau hoàn toàn
    if (routeSegment !== currentSegment) {
      return false
    }
  }

  return true
}

/**
 * Kiểm tra xem đường dẫn của route có khớp với đường dẫn mục tiêu hay không.
 *
 * @param routePath - Đường dẫn định nghĩa của route
 * @param targetPath - Đường dẫn mục tiêu thực tế
 * @returns Có khớp hay không
 */
export const matchRoutePath = (routePath: string, targetPath: string): boolean => {
  return matchPathname(routePath, targetPath)
}

/**
 * Xây dựng bộ nhớ đệm (cache) cho menu:
 * - pathMap: path -> Đối tượng menu
 * - ancestorsMap: path -> Chuỗi các path cha (sử dụng cho openKeys)
 * - routeToMenuPathMap: path của route -> path của menu hiển thị tương ứng (sử dụng khi menuType === 3)
 */
export function buildMenuCaches(menuList: MenuEntity[]): MenuCaches {
  const pathMap = new Map<string, MenuEntity>()
  const ancestorsMap = new Map<string, string[]>()
  const routeToMenuPathMap = new Map<string, string>()

  const dfs = (node: MenuEntity, parentVisibleAncestors: string[]) => {
    const isPureRoute = node.meta?.menuType === 2
    const menuKey = getMenuKey(node)

    // pathMap chỉ được đăng ký bằng thuộc tính path, dùng để so khớp pathname; chỉ những nút có path thực tế mới tham gia tìm kiếm đường dẫn
    if (node.path?.trim()) {
      pathMap.set(node.path.trim(), node)
    }

    if (!isPureRoute && !node.hidden) {
      ancestorsMap.set(menuKey, [...parentVisibleAncestors])
    }

    if (isPureRoute) {
      const nearestVisible = parentVisibleAncestors[parentVisibleAncestors.length - 1]
      if (nearestVisible && node.path?.trim()) {
        routeToMenuPathMap.set(node.path.trim(), nearestVisible)
      }
    }

    const nextVisibleAncestors =
      isPureRoute || node.hidden ? parentVisibleAncestors : [...parentVisibleAncestors, menuKey]

    node.children?.forEach((child) => {
      dfs(child, nextVisibleAncestors)
    })
    node.childrenRoute?.forEach((child) => {
      dfs(child as MenuEntity, nextVisibleAncestors)
    })
  }

  menuList.forEach((root) => {
    dfs(root, [])
  })

  return { pathMap, ancestorsMap, routeToMenuPathMap }
}

/**
 * Tìm kiếm đối tượng menu theo đường dẫn
 * @param path Đường dẫn tìm kiếm
 * @param caches Bộ nhớ đệm menu (Menu caches)
 * @returns Đối tượng menu tìm thấy hoặc undefined
 */
export function findMenuByPath(path: string, caches: MenuCaches): MenuEntity | undefined {
  const { pathMap } = caches
  let entity = pathMap.get(path)
  if (!entity) {
    for (const [candidatePath, candidateEntity] of pathMap.entries()) {
      if (matchPathname(candidatePath, path)) {
        entity = candidateEntity
        break
      }
    }
  }
  return entity
}

/**
 * Normalize a part of speech to its short form.
 * e.g. noun -> n, adjective -> adj.
 * @param pos Full or abbreviated part-of-speech name.
 * @returns Standardized abbreviation.
 */
export function simplifyPos(pos: string): string {
  const posMap: Record<string, string> = {
    noun: "n",
    pronoun: "pro",
    verb: "v",
    adjective: "adj",
    adverb: "adv",
    preposition: "prep",
    conjunction: "conj",
    interjection: "int",
    determiner: "det",
    article: "art",
    numeral: "num",
    auxiliary: "aux",
    modal: "mod",
    particle: "part",
  }
  return posMap[pos.trim().toLowerCase()] ?? pos
}

/**
 * Get color tag mapping for CEFR level.
 * @param level CEFR level (A1-C2)
 * @returns Color name string suitable for Ant Design Tag.
 */
export function getCefrColor(level: string): string {
  switch (level) {
    case "A1":
      return "cyan"
    case "A2":
      return "blue"
    case "B1":
      return "green"
    case "B2":
      return "gold"
    case "C1":
      return "orange"
    case "C2":
      return "red"
    default:
      return "default"
  }
}
